import React, { useEffect } from "react";
import { motion, MotionValue, useReducedMotion, useSpring, useTransform } from "framer-motion";

interface JourneyPlantProps {
  /** Growth from 0 (seed) to 4 (fruiting tree); fractional values are in-between shapes */
  stage: number;
  /** Appearing fresh rather than continuing: skip most of the growth from the old stage */
  fresh: boolean;
  className?: string;
}

// Maps growth onto a 0→1 range between two stages (clamped)
const useRange = (growth: MotionValue<number>, from: number, to: number, out: [number, number] = [0, 1]) =>
  useTransform(growth, [from, to], out);

/**
 * One plant that morphs continuously seed → sprout → sapling → tree → fruit,
 * driven by a spring so every change of stage is a visible transformation.
 */
const JourneyPlant: React.FC<JourneyPlantProps> = ({ stage, fresh, className }) => {
  const reduceMotion = useReducedMotion();
  const growth = useSpring(stage, { stiffness: 42, damping: 14, restDelta: 0.001 });

  useEffect(() => {
    if (reduceMotion) {
      growth.jump(stage);
      return;
    }
    // A fresh caption shows a short final burst of growth instead of replaying from an old stage
    if (fresh) growth.jump(Math.max(0, stage - 0.7));
    growth.set(stage);
  }, [stage, fresh, reduceMotion, growth]);

  // Seed: sinks and fades as the sprout breaks through
  const seedScale = useRange(growth, 0, 0.8, [1, 0.55]);
  const seedOpacity = useTransform(growth, [0, 0.45, 0.85], [1, 1, 0]);

  // Stem: shoots up through sprout and sapling, then settles into a short, thick trunk under the canopy
  const stemScale = useTransform(growth, [0.35, 1, 2, 2.9], [0, 0.31, 0.875, 0.45]);
  const stemWidth = useRange(growth, 2, 3, [2, 3.2]);

  // Sprout + sapling leaves unfurl, then give way to the canopy
  const sproutLeaf = useRange(growth, 0.5, 1);
  const saplingLeafLow = useRange(growth, 1.3, 1.8);
  const saplingLeafHigh = useRange(growth, 1.5, 2);
  const saplingBud = useRange(growth, 1.7, 2);
  const leavesOpacity = useRange(growth, 2.4, 2.9, [1, 0]);

  // Tree: branches spread and the canopy swells up from the trunk
  const branches = useRange(growth, 2.3, 2.9);
  const canopyScale = useRange(growth, 2.2, 3, [0.2, 1]);
  const canopyOpacity = useRange(growth, 2.2, 2.5);

  // Fruit: pops in one by one
  const fruit0 = useRange(growth, 3.3, 3.6);
  const fruit1 = useRange(growth, 3.45, 3.75);
  const fruit2 = useRange(growth, 3.6, 3.9);
  const fruit3 = useRange(growth, 3.7, 4);

  const fruits: [number, number, MotionValue<number>][] = [
    [18, 20, fruit0],
    [29.5, 14.5, fruit1],
    [24.5, 25, fruit2],
    [31, 23.5, fruit3],
  ];

  return (
    <svg
      viewBox="0 0 48 48"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={`journey-plant ${className ?? ""}`}
      aria-hidden="true"
    >
      {/* Soil */}
      <path d="M10 42h28" />
      <path d="M17.5 42c2-2.2 11-2.2 13 0" opacity={0.5} />

      <motion.path
        d="M24 41c-2.6 0-4-1.7-4-3.7S22 33.7 24 33c2 .7 4 2.8 4 4.8S26.6 41 24 41z"
        className="journey-plant__fill"
        style={{ scale: seedScale, opacity: seedOpacity, originX: 0.5, originY: 1 }}
      />

      <motion.path d="M24 42V10" style={{ scaleY: stemScale, strokeWidth: stemWidth, originY: 1 }} />

      {/* Branches */}
      <motion.path d="M24 36l-4.5-4" style={{ scale: branches, originX: 1, originY: 1 }} />
      <motion.path d="M24 34l5-4.5" style={{ scale: branches, originX: 0, originY: 1 }} />

      {/* Canopy */}
      <motion.path
        d="M15.5 31A7.5 7.5 0 0 1 13 17.5a11 11 0 0 1 22 0A7.5 7.5 0 0 1 32.5 31z"
        className="journey-plant__canopy"
        style={{ scale: canopyScale, opacity: canopyOpacity, originX: 0.5, originY: 1 }}
      />

      {/* Sprout leaves */}
      <motion.g style={{ opacity: leavesOpacity }}>
        <motion.path
          d="M24 32c0-3.5-2.7-6-6.5-6 0 3.5 2.7 6 6.5 6z"
          className="journey-plant__fill"
          style={{ scale: sproutLeaf, originX: 1, originY: 1 }}
        />
        <motion.path
          d="M24 31c0-3.8 2.9-6.5 7-6.5 0 3.8-2.9 6.5-7 6.5z"
          className="journey-plant__fill"
          style={{ scale: sproutLeaf, originX: 0, originY: 1 }}
        />

        {/* Sapling leaves */}
        <motion.path
          d="M24 24c0-3.2-2.5-5.4-6-5.4 0 3.2 2.5 5.4 6 5.4z"
          className="journey-plant__fill"
          style={{ scale: saplingLeafLow, originX: 1, originY: 1 }}
        />
        <motion.path
          d="M24 19c0-3.2 2.5-5.4 6-5.4 0 3.2-2.5 5.4-6 5.4z"
          className="journey-plant__fill"
          style={{ scale: saplingLeafHigh, originX: 0, originY: 1 }}
        />
        <motion.path
          d="M24 14c-1.2-2.6-.5-5.2 1.9-6.8 1.2 2.6.5 5.2-1.9 6.8z"
          className="journey-plant__fill"
          style={{ scale: saplingBud, originX: 0, originY: 1 }}
        />
      </motion.g>

      {fruits.map(([cx, cy, scale]) => (
        <motion.circle
          key={`${cx}-${cy}`}
          cx={cx}
          cy={cy}
          r={2}
          className="journey-plant__fruit"
          style={{ scale, originX: 0.5, originY: 0.5 }}
        />
      ))}
    </svg>
  );
};

export default JourneyPlant;
